import { useState, useEffect } from 'react';
import { SUPPORTED_LOCALES } from '@trip-ux/constants';

/** Supported language type */
export type Lang = SUPPORTED_LOCALES;

/**
 * Country object with ISO 3166-1 alpha-2 code and localized name
 */
export interface Country {
  code: string;
  name: string;
}

// Module-level cache - persists for the SPA lifetime
const cache = new Map<string, Country[]>();
const pending = new Map<string, Promise<Country[]>>();

// Base path resolved from the host app once at boot via `configureCountries`.
// Kept out-of-band so consumers of `useCountries`/`fetchCountries` don't have to
// know about deployment-specific context paths.
let defaultBasePath = '';

/**
 * Configure the base path that `fetchCountries` / `useCountries` prepend to the
 * BFF endpoint. Call this once during app bootstrap with the value from your
 * app config (e.g. `config.options.clientContextPath[locale]`).
 *
 * Kept deployment-agnostic: the library never hardcodes context paths itself.
 */
export function configureCountries(options: { basePath?: string }): void {
  defaultBasePath = options.basePath ?? '';
}

const buildUrl = (lang: Lang, basePath: string): string => {
  const normalized = basePath ? basePath.replace(/\/$/, '') : '';
  return `${normalized}/api/reference/countries?lang=${lang}`;
};

/**
 * Internal fetch helper - performs the actual API call
 */
async function doFetch(lang: Lang, basePath: string): Promise<Country[]> {
  try {
    const response = await fetch(buildUrl(lang, basePath));
    if (!response.ok) {
      throw new Error('countries fetch failed');
    }
    const data = await response.json() as Country[];
    cache.set(lang, data);
    return data;
  } finally {
    pending.delete(lang);
  }
}

/**
 * Fetch countries from BFF endpoint.
 *
 * @param lang - Language code (SUPPORTED_LOCALES.ENGLISH or SUPPORTED_LOCALES.FRENCH)
 * @param basePath - Optional override for the base path. When omitted, the value
 *                   configured via `configureCountries` is used.
 * @returns Array of Country objects sorted alphabetically by name
 */
export async function fetchCountries(lang: Lang, basePath?: string): Promise<Country[]> {
  // Return cached data if available
  const cached = cache.get(lang);
  if (cached) {
    return cached;
  }

  // Dedupe concurrent requests for the same language
  const pendingRequest = pending.get(lang);
  if (pendingRequest) {
    return pendingRequest;
  }

  const promise = doFetch(lang, basePath ?? defaultBasePath);
  pending.set(lang, promise);
  return promise;
}

/**
 * React hook for fetching countries with module-level caching.
 *
 * @param lang - Language code (SUPPORTED_LOCALES.ENGLISH or SUPPORTED_LOCALES.FRENCH)
 * @param basePath - Optional override forwarded to `fetchCountries`.
 * @returns Array of Country objects
 *
 * @example
 * ```tsx
 * import { useCountries } from '@trip-ux/common';
 * import { SUPPORTED_LOCALES } from '@trip-ux/constants';
 *
 * const countries = useCountries(SUPPORTED_LOCALES.ENGLISH);
 * ```
 */
export function useCountries(lang: Lang, basePath?: string): Country[] {
  const [countries, setCountries] = useState<Country[]>(cache.get(lang) ?? []);

  useEffect(() => {
    let cancelled = false;

    const cached = cache.get(lang);
    if (cached) {
      setCountries(cached);
      return;
    }

    (async () => {
      try {
        const data = await fetchCountries(lang, basePath);
        if (!cancelled) {
          setCountries(data);
        }
      } catch {
        // Component shows empty list on error
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, basePath]);

  return countries;
}

/**
 * Clear the countries cache (useful for testing)
 */
export function clearCountriesCache(): void {
  cache.clear();
  pending.clear();
  defaultBasePath = '';
}
